/**
 * AB Test Calculator - Refactored version using modular components
 * 
 * Component Structure:
 * - ABTestCalculator (main component) - Tab management and state
 * - PreTestAnalysis - Test planning and sample size calculations  
 * - PostTestAnalysis - Test results analysis
 * - VariantInputForm - Variant data input
 * - AnalysisSettings - Analysis configuration
 * - ResultsTable - Results display
 * - ChartModal - Probability density charts
 */

import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Typography,
  Tab,
  Tabs,
  Box,
  Link,
  Chip,
  Button,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LoginIcon from '@mui/icons-material/Login';
import { METHODS_GUIDE_BAYESIAN, METHODS_GUIDE_URL } from '../components/dashboard/SignificanceGuideHint';
import { updateReportMetadata } from '../services/reportService';
import {
  Assessment as AssessmentIcon,
  Calculate as CalculateIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import showNotification from '../components/common/Notification';
import {
  calculateSampleSize,
  calculateMDE,
  calculateTestDuration,
  zTestProportions,
  chiSquaredTest,
  enhancedBayesianABTest,
  calculateAdditionalDaysNeeded,
  calculateExtraTransactions,
  calculateOverallWinnerProbabilities,
  conversionRateConfidenceInterval,
  calculateStatisticalPower,
} from '../utils/statisticsUtils';
import {
  PreTestAnalysis,
  PostTestAnalysis,
  ChartModal,
} from '../components/calculator';
import PlanTestDialog from '../components/calculator/PlanTestDialog';
import { useAuth } from '../hooks/useAuth';
import { BRAND } from '../theme/brandTheme';
import {
  SAMPLE_POST_TEST,
  SAMPLE_PRE_TEST,
  isPostTestFormEmpty,
} from '../utils/calculatorSampleData';
import '../components/calculator/chartSetup';
import '../styles/calculator-page.css';

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const ABTestCalculator = ({ layout = 'dashboard' }) => {
  const isPublic = layout === 'public';
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);

  // Pre-test Analysis State
  const [preTestData, setPreTestData] = useState({
    baselineRate: 5,
    minDetectableEffect: 20,
    alpha: 0.05,
    power: 0.8,
    dailyTraffic: 1000,
    trafficSplit: 50
  });

  const [preTestResults, setPreTestResults] = useState(null);

  // Post-test Analysis State
  const [variants, setVariants] = useState([
    { name: 'Control', sessions: '', conversions: '', isControl: true },
    { name: 'Variant A', sessions: '', conversions: '', isControl: false }
  ]);

  const [postTestSettings, setPostTestSettings] = useState({
    analysisMethod: 'bayesian',
    confidenceLevel: 0.95,
    testDuration: 14,
    dailyTraffic: 1000,
    trafficSplit: 50,
    averageOrderValue: 0
  });

  const [postTestResults, setPostTestResults] = useState(null);
  const [enhancedResults, setEnhancedResults] = useState(null);
  const [additionalDaysData, setAdditionalDaysData] = useState(null);
  const [extraTransactionsData, setExtraTransactionsData] = useState(null);
  
  // Chart Modal State
  const [selectedVariantChart, setSelectedVariantChart] = useState(null);

  // State to track if data was imported from report
  const [dataImported, setDataImported] = React.useState(false);

  // Ref for results table to scroll to
  const resultsTableRef = React.useRef(null);

  // Check for imported data from report on component mount
  React.useEffect(() => {
    const importedData = localStorage.getItem('calculatorImportData');
    if (importedData) {
      try {
        const data = JSON.parse(importedData);
        
        // Set post-test settings first
        if (data.postTestSettings) {
          setPostTestSettings(prevSettings => ({
            ...prevSettings,
            ...data.postTestSettings
          }));
        }
        
        // Switch to post-test analysis tab (index 0)
        setActiveTab(0);
        
        // Set variants last and mark as imported
        if (data.variants && Array.isArray(data.variants)) {
          setVariants(data.variants);
          setDataImported(true);
        }

        // Sync AOV back to source report when imported from dashboard
        if (data.sourceReportId && data.postTestSettings?.averageOrderValue > 0) {
          updateReportMetadata(data.sourceReportId, {
            averageOrderValue: data.postTestSettings.averageOrderValue,
          }).catch(() => {});
        }
        
        // Clear localStorage after importing
        localStorage.removeItem('calculatorImportData');
      } catch (error) {
        console.error('Error importing calculator data:', error);
      }
    }
  }, []);

  // Auto-calculate when variants change after data import
  React.useEffect(() => {
    if (dataImported && variants.length >= 2) {
      // Add a small delay to ensure all state updates are complete
      const timer = setTimeout(() => {
        calculatePostTestMetrics();
        setDataImported(false); // Reset flag
      }, 100);
      
      return () => clearTimeout(timer);
    }
    // Intentionally omit calculatePostTestMetrics — only run after import
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants, dataImported]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset results when switching tabs
    if (newValue === 0) {
      setPostTestResults(null);
      setEnhancedResults(null);
      setAdditionalDaysData(null);
      setExtraTransactionsData(null);
    } else {
      setPreTestResults(null);
    }
  };

  // Pre-test Analysis Functions
  const handlePreTestChange = (field, value) => {
    setPreTestData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const calculatePreTestMetrics = (dataOverride) => {
    setLoading(true);

    const data = dataOverride ?? preTestData;

    try {
      const baselineRateDecimal = data.baselineRate / 100;
      const mdeDecimal = data.minDetectableEffect / 100;

      const sampleSize = calculateSampleSize(
        baselineRateDecimal,
        mdeDecimal,
        data.alpha,
        data.power,
        2,
      );

      const mdeResults = calculateMDE(
        sampleSize,
        baselineRateDecimal,
        data.alpha,
        data.power,
        2,
      );

      const durationResults = calculateTestDuration(
        sampleSize,
        data.dailyTraffic,
        data.trafficSplit / 100,
      );

      const power = calculateStatisticalPower(
        sampleSize,
        baselineRateDecimal,
        mdeDecimal,
        data.alpha,
      );

      setPreTestResults({
        sampleSize,
        sampleSizePerVariant: Math.ceil(sampleSize / 2),
        mde: mdeResults,
        duration: durationResults,
        power: power * 100,
        confidenceLevel: (1 - data.alpha) * 100,
      });

    } catch (error) {
      console.error('Pre-test calculation error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Post-test Analysis Functions
  const addVariant = () => {
    const newVariant = {
      name: `Variant ${String.fromCharCode(65 + variants.length - 1)}`,
      sessions: '',
      conversions: '',
      isControl: false
    };
    setVariants([...variants, newVariant]);
  };

  const removeVariant = (index) => {
    if (variants.length > 2 && !variants[index].isControl) {
      setVariants(variants.filter((_, i) => i !== index));
    }
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index][field] = value;
    setVariants(newVariants);
  };

  const handlePostTestSettingsChange = (field, value) => {
    setPostTestSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /** Fill post-test form with demo data and run analysis */
  const handleLoadSamplePostTest = () => {
    const mergedSettings = { ...postTestSettings, ...SAMPLE_POST_TEST.settings };
    setVariants(SAMPLE_POST_TEST.variants);
    setPostTestSettings(mergedSettings);
    setPostTestResults(null);
    calculatePostTestMetrics({
      variants: SAMPLE_POST_TEST.variants,
      postTestSettings: mergedSettings,
    });
    showNotification('Örnek test verileri yüklendi', 'success');
  };

  /** Fill pre-test form with demo params and run planning calc */
  const handleLoadSamplePreTest = () => {
    setPreTestData(SAMPLE_PRE_TEST);
    setPreTestResults(null);
    calculatePreTestMetrics(SAMPLE_PRE_TEST);
    showNotification('Örnek planlama verileri yüklendi', 'success');
  };

  const showSamplePostTestButton = isPostTestFormEmpty(variants) && !postTestResults;

  const calculatePostTestMetrics = (overrides = {}) => {
    const activeVariants = overrides.variants ?? variants;
    const settings = overrides.postTestSettings ?? postTestSettings;

    setLoading(true);

    try {
      const validVariants = activeVariants.filter(
        (v) =>
          v.sessions &&
          v.conversions &&
          parseInt(v.sessions, 10) > 0 &&
          parseInt(v.conversions, 10) >= 0,
      );

      if (validVariants.length < 2) {
        showNotification('En az 2 varyant için geçerli veri girmeniz gerekiyor', 'error');
        setLoading(false);
        return;
      }

      const control = validVariants.find(v => v.isControl) || validVariants[0];
      const testVariants = validVariants.filter(v => !v.isControl || v !== control);

      const results = {
        control: {
          ...control,
          sessions: parseInt(control.sessions),
          conversions: parseInt(control.conversions),
          conversionRate: parseInt(control.conversions) / parseInt(control.sessions),
          confidenceInterval: conversionRateConfidenceInterval(
            parseInt(control.conversions, 10),
            parseInt(control.sessions, 10),
            settings.confidenceLevel,
          ),
        },
        variants: []
      };

      let allEnhancedResults = [];
      let allAdditionalDaysData = [];
      let allExtraTransactionsData = [];

      let overallWinnerProbabilities = null;
      if (settings.analysisMethod === 'bayesian') {
        const allVariantsForWinnerCalc = [
          {
            name: control.name,
            conversions: parseInt(control.conversions),
            sessions: parseInt(control.sessions),
            isControl: true
          },
          ...testVariants.map(v => ({
            name: v.name,
            conversions: parseInt(v.conversions),
            sessions: parseInt(v.sessions),
            isControl: false
          }))
        ];
        
        overallWinnerProbabilities = calculateOverallWinnerProbabilities(allVariantsForWinnerCalc);
      }

      testVariants.forEach((variant, variantIndex) => {
        const sessions = parseInt(variant.sessions);
        const conversions = parseInt(variant.conversions);
        const conversionRate = conversions / sessions;
        
        const lift = (conversionRate - results.control.conversionRate) / results.control.conversionRate;
        
        let statisticalTest = null;
        let enhancedBayesian = null;
        let additionalDays = null;
        let extraTransactions = null;

        if (settings.analysisMethod === 'bayesian') {
          enhancedBayesian = enhancedBayesianABTest(
            results.control.conversions,
            results.control.sessions,
            conversions,
            sessions
          );
          
          statisticalTest = {
            pValue: enhancedBayesian.probabilityVariantWins,
            isSignificant: enhancedBayesian.isSignificant,
            winnerProbabilities: enhancedBayesian.winnerProbabilities,
            densityData: enhancedBayesian.densityData
          };
          
          allEnhancedResults.push({
            variantName: variant.name,
            ...enhancedBayesian
          });
        } else if (settings.analysisMethod === 'ztest') {
          statisticalTest = zTestProportions(
            conversionRate,
            sessions,
            results.control.conversionRate,
            results.control.sessions,
            1 - settings.confidenceLevel,
          );
        } else if (settings.analysisMethod === 'chisquared') {
          const observed = [conversions, sessions - conversions, results.control.conversions, results.control.sessions - results.control.conversions];
          const total = sessions + results.control.sessions;
          const totalConversions = conversions + results.control.conversions;
          const expectedConversionRate = totalConversions / total;
          
          const expected = [
            sessions * expectedConversionRate,
            sessions * (1 - expectedConversionRate),
            results.control.sessions * expectedConversionRate,
            results.control.sessions * (1 - expectedConversionRate)
          ];
          
          statisticalTest = chiSquaredTest(observed, expected, 1 - settings.confidenceLevel);
        }

        if (settings.dailyTraffic > 0) {
          additionalDays = calculateAdditionalDaysNeeded(
            results.control.conversions,
            results.control.sessions,
            conversions,
            sessions,
            settings.testDuration,
            settings.dailyTraffic,
            settings.trafficSplit / 100,
          );
          
          allAdditionalDaysData.push({
            variantName: variant.name,
            ...additionalDays
          });
        }

        if (settings.dailyTraffic > 0) {
          extraTransactions = calculateExtraTransactions(
            results.control.conversions,
            results.control.sessions,
            conversions,
            sessions,
            settings.dailyTraffic,
            settings.trafficSplit / 100,
            settings.averageOrderValue || null,
            settings.testDuration || null,
          );
          
          allExtraTransactionsData.push({
            variantName: variant.name,
            ...extraTransactions
          });
        }

        results.variants.push({
          ...variant,
          sessions,
          conversions,
          conversionRate,
          lift,
          liftPercentage: lift * 100,
          confidenceInterval: conversionRateConfidenceInterval(
            conversions,
            sessions,
            settings.confidenceLevel,
          ),
          statisticalTest,
          enhancedBayesian,
          additionalDays,
          extraTransactions
        });
      });

      setEnhancedResults(allEnhancedResults);
      setAdditionalDaysData(allAdditionalDaysData);
      setExtraTransactionsData(allExtraTransactionsData);

      results.overallWinnerProbabilities = overallWinnerProbabilities;

      setPostTestResults(results);

      // Scroll to results table smoothly after calculation
      setTimeout(() => {
        if (resultsTableRef.current) {
          const elementPosition = resultsTableRef.current.offsetTop;
          const offsetPosition = elementPosition - 200; // 200px yukarıdan boşluk bırak
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);

    } catch (error) {
      console.error('Post-test calculation error:', error);
      showNotification('Hesaplama sırasında bir hata oluştu. Lütfen girdiğiniz verileri kontrol edin', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createProbabilityDensityChart = (variantData) => {
    if (!variantData?.enhancedBayesian?.densityData) return null;

    const { xValues, controlDensity, variantDensity } = variantData.enhancedBayesian.densityData;

    return {
      labels: xValues.map(x => (x * 100).toFixed(1)),
      datasets: [
        {
          label: 'Control',
          data: controlDensity,
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          fill: true,
          tension: 0.4
        },
        {
          label: variantData.name,
          data: variantDensity,
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Probability Density Distribution'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(4)}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Conversion Rate (%)'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Probability Density'
        }
      }
    }
  };

  const exportResults = () => {
    const results = activeTab === 1 ? preTestResults : {
      postTestResults,
      enhancedResults,
      additionalDaysData,
      extraTransactionsData
    };
    if (!results) return;

    const dataStr = JSON.stringify(results, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `ab-test-${activeTab === 1 ? 'pretest' : 'posttest'}-results.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    if (activeTab === 1) {
      setPreTestData({
        baselineRate: 5,
        minDetectableEffect: 20,
        alpha: 0.05,
        power: 0.8,
        dailyTraffic: 1000,
        trafficSplit: 50
      });
      setPreTestResults(null);
    } else {
      setVariants([
        { name: 'Control', sessions: '', conversions: '', isControl: true },
        { name: 'Variant A', sessions: '', conversions: '', isControl: false }
      ]);
      setPostTestResults(null);
      setEnhancedResults(null);
      setAdditionalDaysData(null);
      setExtraTransactionsData(null);
    }
  };

  const handleShowChart = (variant) => {
    const chartData = createProbabilityDensityChart(variant);
    if (chartData) {
      setSelectedVariantChart({
        variant: variant,
        chartData: chartData
      });
    }
  };

  const calculatorContent = (
    <>
      {/* Main tabs */}
      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fff' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="AB Test Calculator Tabs"
            sx={{
              px: 1,
              '& .MuiTab-root': {
                fontFamily: BRAND.fontFamily,
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 56,
              },
            }}
          >
            <Tab label="Sonuç Analizi" icon={<CalculateIcon />} iconPosition="start" />
            <Tab label="Test Planlama" icon={<TimelineIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <PostTestAnalysis
            variants={variants}
            postTestSettings={postTestSettings}
            postTestResults={postTestResults}
            loading={loading}
            onVariantChange={handleVariantChange}
            onAddVariant={addVariant}
            onRemoveVariant={removeVariant}
            onSettingsChange={handlePostTestSettingsChange}
            onCalculate={calculatePostTestMetrics}
            onClear={clearAll}
            onShowChart={handleShowChart}
            resultsTableRef={resultsTableRef}
            showSampleButton={showSamplePostTestButton}
            onLoadSampleData={handleLoadSamplePostTest}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <PreTestAnalysis
            preTestData={preTestData}
            preTestResults={preTestResults}
            loading={loading}
            onPreTestChange={handlePreTestChange}
            onCalculate={calculatePreTestMetrics}
            onClear={clearAll}
            onExport={exportResults}
            showSampleButton={!preTestResults}
            onLoadSampleData={handleLoadSamplePreTest}
            showPlanButton={!isPublic && isAuthenticated && !!preTestResults}
            onPlanTest={() => setPlanDialogOpen(true)}
          />
        </TabPanel>
      </Card>

      <ChartModal
        selectedVariantChart={selectedVariantChart}
        onClose={() => setSelectedVariantChart(null)}
        chartOptions={chartOptions}
      />

      <PlanTestDialog
        open={planDialogOpen}
        onClose={() => setPlanDialogOpen(false)}
        preTestData={preTestData}
        preTestResults={preTestResults}
        onCreated={(report) => {
          if (report?._id) navigate(`/dashboard/reports/${report._id}`);
        }}
      />

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Link
          component={RouterLink}
          to={METHODS_GUIDE_URL}
          sx={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: BRAND.purple,
          }}
        >
          Hesaplama Yöntemleri Rehberi →
        </Link>
      </Box>
    </>
  );

  const dashboardHeader = (
    <Box sx={{ mb: 4 }}>
      <Chip
        label="hype CRO Araçları"
        size="small"
        sx={{ mb: 1.5, bgcolor: `${BRAND.purple}14`, color: BRAND.purple, fontWeight: 600 }}
      />
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontWeight: 700, color: BRAND.purple, fontFamily: BRAND.fontFamily }}
      >
        <AssessmentIcon sx={{ mr: 1.5, verticalAlign: 'middle' }} />
        AB Test Hesaplayıcı
      </Typography>
      <Typography variant="body1" sx={{ color: BRAND.textMuted, fontFamily: BRAND.fontFamily }}>
        Test planlama ve sonuç analizi — grafiksel raporlama dahil
      </Typography>
      <Link
        component={RouterLink}
        to={METHODS_GUIDE_BAYESIAN}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 1,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: BRAND.purple,
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: 18 }} />
        Bu nasıl hesaplanıyor?
      </Link>
    </Box>
  );

  if (isPublic) {
    return (
      <>
        <header className="calculator-page-hero">
          <div className="calculator-page-hero__inner">
            <span className="calculator-page-hero__badge">Ücretsiz · Kayıt gerektirmez</span>
            <h1 className="calculator-page-hero__title">AB Test Hesaplayıcı</h1>
            <p className="calculator-page-hero__subtitle">
              Bayesian, Z-Test ve Chi-Squared yöntemleriyle test sonuçlarınızı analiz edin.
              Dönüşüm oranı, lift ve kazanma olasılıklarını grafiklerle görün.
            </p>
            <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to={METHODS_GUIDE_BAYESIAN}
                variant="outlined"
                size="small"
                startIcon={<HelpOutlineIcon />}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.5)',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Yöntemler rehberi
              </Button>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                size="small"
                startIcon={<LoginIcon />}
                sx={{
                  bgcolor: BRAND.green,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#089a1f', boxShadow: 'none' },
                }}
              >
                Dashboard&apos;a giriş
              </Button>
            </Box>
          </div>
        </header>
        <div className="calculator-page-body">
          <div className="calculator-page-body__inner">{calculatorContent}</div>
        </div>
      </>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {dashboardHeader}
      {calculatorContent}
    </Container>
  );
};

export default ABTestCalculator; 